/**
 * SunGrow's fault-code FAMILIES, for wording — which kind of problem a code is, and which string
 * it names. The LABEL of each code comes from the API (the catalogue, migration 0088); this file
 * only decides how a person should read it.
 *
 * Source: SunGrow "Communication Protocol of PV Grid-Connected String Inverters" V1.1.53
 * (2023-01-17), Appendix 1 and Appendix 3. The backend carries the full per-code table
 * (`lumen-be/src/lib/vendors/sungrow-codes.ts`, which generates migration 0088); this is the
 * same grouping at family level, and the two must change together when the protocol version
 * does. They cannot share a module — the repositories do not import each other — so the family
 * ranges are repeated here and pinned by `alarm-words.test.ts`.
 */

export type CodeClass = 'fault' | 'alarm';

export type SungrowFamily =
  | 'grid-overvoltage'
  | 'grid-undervoltage'
  | 'grid-overfrequency'
  | 'grid-underfrequency'
  | 'grid-outage'
  | 'leakage'
  | 'grid-abnormal'
  | 'grid-imbalance'
  | 'pv-reverse-fault'
  | 'pv-reverse-alarm'
  | 'pv-abnormal'
  | 'temp-high'
  | 'temp-low'
  | 'insulation'
  | 'grounding-cable'
  | 'arc'
  | 'meter-reverse'
  | 'meter-comms'
  | 'grid-confrontation'
  | 'parallel-comms'
  | 'system-fault'
  | 'system-alarm'
  | 'mppt-reverse'
  | 'boost-alarm'
  | 'boost-fault'
  | 'reflux'
  | 'pv-grounding'
  | 'hardware';

type Span = number | readonly [number, number];

interface FamilyDef {
  readonly family: SungrowFamily;
  /** Appendix 3's own name — shown small, beside the code. */
  readonly vendor: string;
  readonly cls: CodeClass;
  readonly codes: readonly Span[];
  /** Runs numbered per string/MPPT: [first code, last code, number of the first unit]. */
  readonly per?: { readonly noun: 'string' | 'MPPT input'; readonly runs: ReadonlyArray<readonly [number, number, number]> };
}

export const SUNGROW_FAMILIES: readonly FamilyDef[] = [
  { family: 'grid-overvoltage', vendor: 'Grid overvoltage', cls: 'fault', codes: [2, 3, 14, 15] },
  { family: 'grid-undervoltage', vendor: 'Grid undervoltage', cls: 'fault', codes: [4, 5] },
  { family: 'grid-overfrequency', vendor: 'Grid overfrequency', cls: 'fault', codes: [8] },
  { family: 'grid-underfrequency', vendor: 'Grid underfrequency', cls: 'fault', codes: [9] },
  { family: 'grid-outage', vendor: 'Grid power outage', cls: 'fault', codes: [10] },
  { family: 'leakage', vendor: 'Excess leakage current', cls: 'fault', codes: [12] },
  { family: 'grid-abnormal', vendor: 'Grid abnormal', cls: 'fault', codes: [13] },
  { family: 'grid-imbalance', vendor: 'Grid voltage imbalance', cls: 'fault', codes: [17] },
  {
    family: 'pv-reverse-fault',
    vendor: 'PV reverse connection fault',
    cls: 'fault',
    codes: [28, 29, 208, [448, 479]],
    per: { noun: 'string', runs: [[448, 479, 1]] },
  },
  {
    family: 'pv-reverse-alarm',
    vendor: 'PV reverse connection alarm',
    cls: 'alarm',
    codes: [[532, 547], [564, 579]],
    per: { noun: 'string', runs: [[532, 547, 1], [564, 579, 17]] },
  },
  {
    family: 'pv-abnormal',
    vendor: 'PV abnormal alarm',
    cls: 'alarm',
    codes: [[548, 563], [580, 595]],
    per: { noun: 'string', runs: [[548, 563, 1], [580, 595, 17]] },
  },
  { family: 'temp-high', vendor: 'Excessively high ambient temperature', cls: 'fault', codes: [37] },
  { family: 'temp-low', vendor: 'Excessively low ambient temperature', cls: 'fault', codes: [43] },
  { family: 'insulation', vendor: 'Low system insulation resistance', cls: 'fault', codes: [39] },
  { family: 'grounding-cable', vendor: 'Grounding cable fault', cls: 'fault', codes: [106] },
  { family: 'arc', vendor: 'Electric arc fault', cls: 'fault', codes: [88] },
  { family: 'meter-reverse', vendor: 'Reverse connection alarm of the meter/CT', cls: 'alarm', codes: [84] },
  { family: 'meter-comms', vendor: 'Meter communication abnormal alarm', cls: 'alarm', codes: [514] },
  { family: 'grid-confrontation', vendor: 'Grid confrontation', cls: 'fault', codes: [323] },
  { family: 'parallel-comms', vendor: 'Inverter parallel communication alarm', cls: 'alarm', codes: [75] },
  {
    family: 'system-fault',
    vendor: 'System fault',
    cls: 'fault',
    codes: [
      7, 11, 16, [19, 25], [30, 34], 36, 38, [40, 42], [44, 50], [52, 58], [60, 68], 85, 87, 92, 93,
      [100, 105], [107, 114], [116, 124], [200, 207], [209, 211], [248, 255], [300, 322], [324, 326],
      [401, 412], [600, 603], 605, 608, 612, 616, 620, [622, 624], 800, 802, 804, 807, [1096, 1122],
    ],
  },
  {
    family: 'system-alarm',
    vendor: 'System alarm',
    cls: 'alarm',
    codes: [
      59, [70, 72], 74, 76, [77, 81], 82, 83, 89, [216, 218], [220, 231], [432, 434], [500, 513],
      [515, 518], 900, 901, 910, 911, [635, 638],
    ],
  },
  {
    family: 'mppt-reverse',
    vendor: 'MPPT reverse connection',
    cls: 'fault',
    codes: [[264, 283]],
    per: { noun: 'MPPT input', runs: [[264, 283, 1]] },
  },
  { family: 'boost-alarm', vendor: 'Boost capacitor overvoltage alarm', cls: 'alarm', codes: [[332, 363]] },
  { family: 'boost-fault', vendor: 'Boost capacitor overvoltage fault', cls: 'fault', codes: [[364, 395]] },
  {
    family: 'reflux',
    vendor: 'String current reflux',
    cls: 'fault',
    codes: [[1548, 1579]],
    per: { noun: 'string', runs: [[1548, 1579, 1]] },
  },
  { family: 'pv-grounding', vendor: 'PV grounding fault', cls: 'fault', codes: [[1600, 1611]] },
  { family: 'hardware', vendor: 'System hardware fault', cls: 'fault', codes: [1616] },
];

export interface SungrowCodeInfo {
  readonly code: number;
  readonly hex: string;
  readonly family: SungrowFamily;
  readonly vendor: string;
  readonly cls: CodeClass;
  readonly unit: { readonly noun: 'string' | 'MPPT input'; readonly n: number } | null;
}

export function hex4(code: number): string {
  return `0x${(code & 0xffff).toString(16).toUpperCase().padStart(4, '0')}`;
}

function inSpan(code: number, s: Span): boolean {
  return typeof s === 'number' ? code === s : code >= s[0] && code <= s[1];
}

/** The family a fault code belongs to, or null when SunGrow's list does not hold it. */
export function sungrowFaultCode(code: number): SungrowCodeInfo | null {
  for (const f of SUNGROW_FAMILIES) {
    if (!f.codes.some((s) => inSpan(code, s))) continue;
    let unit: SungrowCodeInfo['unit'] = null;
    for (const [first, last, base] of f.per?.runs ?? []) {
      if (code >= first && code <= last) unit = { noun: f.per!.noun, n: base + (code - first) };
    }
    return { code, hex: hex4(code), family: f.family, vendor: f.vendor, cls: f.cls, unit };
  }
  return null;
}

/**
 * Appendix 1 work states, keyed by the value the gateway SENDS (signed 16-bit — every code
 * ≥ 0x8000 arrives negative) and by its unsigned form, in case the decode is fixed upstream.
 */
export type WorkStateKind = 'alarm-run' | 'fault-run' | 'emergency-stop' | 'comms-fault' | 'derating' | 'stop';

const WORK_STATES: ReadonlyArray<readonly [readonly number[], WorkStateKind, string, CodeClass]> = [
  [[-28416, 37120], 'alarm-run', 'Alarm run', 'alarm'],
  [[21760], 'fault-run', 'Fault', 'fault'],
  [[5376], 'emergency-stop', 'Emergency stop', 'fault'],
  [[9472], 'comms-fault', 'Communication fault', 'fault'],
  [[-32512, 33024], 'derating', 'Derating run', 'alarm'],
  [[-32768, 32768], 'stop', 'Stop', 'alarm'],
];

export function sungrowWorkState(
  value: number,
): { kind: WorkStateKind; vendor: string; hex: string; cls: CodeClass } | null {
  for (const [values, kind, vendor, cls] of WORK_STATES) {
    if (values.includes(value)) return { kind, vendor, hex: hex4(value), cls };
  }
  return null;
}

export const ALARM_RUN_VALUES: readonly number[] = [-28416, 37120];
